import { ClassificationResult, ExtractedCandidatesPayload } from './report-request';

export type EnquiryStage = 'received' | 'triaged' | 'resolved';

export type EnquiryResolution =
  | 'converted'
  | 'quote_only'
  | 'out_of_scope'
  | 'spam'
  | 'merged'
  | 'other';

/**
 * Enquiry-level metadata that does NOT belong on any individual report
 * (e.g. "quote needed by midday" applies to the enquiry, not to a single
 * report's TAT). Free-form because the LLM extractor may surface new
 * fields over time; the analyst UI defaults to rendering known keys.
 */
export interface EnquiryMetadata {
  quote_required?: boolean;
  quote_deadline?: string | null;
  free_text_asks?: string | null;
  [key: string]: unknown;
}

/**
 * Raw payload Freshdesk delivered. Same liberal shape as
 * `FreshdeskRawPayload` on `report-request.ts`. The webhook ingestion
 * worker overlays canonical `subject` / `description_text` after fetching
 * the full ticket from the Freshdesk API.
 */
export interface EnquiryRawPayload {
  id?: number;
  subject?: string | null;
  description?: string | null;
  description_text?: string | null;
  requester_email?: string | null;
  [key: string]: unknown;
}

export interface EnquirySummary {
  id: string;
  source: string;
  sourceExternalId: string | null;
  teamId: string | null;
  stage: EnquiryStage;
  resolutionCategory: EnquiryResolution | null;
  candidateCount: number;
  /** Composed codename `{human_part}-{pepper}`. NULL only for legacy
   * rows that pre-date the Phase-03 backfill. */
  codename: string | null;
  /** Rename key for PATCH /codenames/{family_key}. For an enquiry this
   * mirrors `codename` — never derive it by splitting. */
  codenameFamily: string | null;
  createdAt: string | null;
  resolvedAt: string | null;
}

export interface EnquiryDetail {
  id: string;
  source: string;
  sourceExternalId: string | null;
  teamId: string | null;
  stage: EnquiryStage;
  resolutionCategory: EnquiryResolution | null;
  resolutionNotes: string | null;
  mergedIntoId: string | null;
  /** Composed codename — also the family key for renames. */
  codename: string | null;
  /** True when an analyst manually renamed the codename. */
  codenameIsCustom: boolean;
  /** The team's backoffice client code, when it resolves to one. */
  clientCode: string | null;
  rawPayload: EnquiryRawPayload;
  classificationResult: ClassificationResult | null;
  extractedCandidates: ExtractedCandidatesPayload | null;
  enquiryMetadata: EnquiryMetadata | null;
  createdAt: string | null;
  updatedAt: string | null;
  resolvedAt: string | null;
  reportRequestIds: string[];
  /** Rich child summaries — same rows as reportRequestIds, ordered by
   * codename suffix. Powers the family chain + Requests rail card. */
  reportRequests: EnquiryChildRequest[];
}

export interface EnquiryConvertCandidate {
  candidate_index: number;
  report_config_id: string;
  turnaround_time_days?: number | null;
  price?: number | null;
  expedited?: boolean;
  subjects?: string[] | null;
  jurisdictions?: string[] | null;
}

export interface EnquiryConvertRequest {
  candidates: EnquiryConvertCandidate[];
  team_id?: string | null;
}

export interface EnquiryConvertResponse {
  enquiry_id: string;
  report_request_ids: string[];
}

export interface EnquiryChildRequest {
  id: string;
  codename: string | null;
  triageStage: string;
  subjectStrings: string[] | null;
  reportConfigId: string | null;
  configName: string | null;
  expedited: boolean;
  reportId: string | null;
  reportLifecycleStage: string | null;
  /** The report is an evaluation copy of this case. */
  evaluationOfReportId: string | null;
  createdAt: string | null;
}

export interface ApiEnquiryChildRequest {
  id: string;
  codename?: string | null;
  triage_stage: string;
  subject_strings?: string[] | null;
  report_config_id?: string | null;
  config_name?: string | null;
  expedited?: boolean;
  report_id?: string | null;
  report_lifecycle_stage?: string | null;
  evaluation_of_report_id?: string | null;
  created_at?: string | null;
}

export function transformToEnquiryChildRequest(api: ApiEnquiryChildRequest): EnquiryChildRequest {
  return {
    id: api.id,
    codename: api.codename ?? null,
    triageStage: api.triage_stage,
    subjectStrings: api.subject_strings ?? null,
    reportConfigId: api.report_config_id ?? null,
    configName: api.config_name ?? null,
    expedited: api.expedited ?? false,
    reportId: api.report_id ?? null,
    reportLifecycleStage: api.report_lifecycle_stage ?? null,
    evaluationOfReportId: api.evaluation_of_report_id ?? null,
    createdAt: api.created_at ?? null,
  };
}

export interface EnquiryResolveRequest {
  category: EnquiryResolution;
  notes?: string | null;
  merged_into_id?: string | null;
}

// API (snake_case) mirrors

export interface ApiEnquirySummary {
  id: string;
  source: string;
  source_external_id: string | null;
  team_id: string | null;
  stage: EnquiryStage;
  resolution_category: EnquiryResolution | null;
  candidate_count: number;
  codename: string | null;
  codename_family?: string | null;
  created_at: string | null;
  resolved_at: string | null;
}

export interface ApiEnquiryDetail {
  id: string;
  source: string;
  source_external_id: string | null;
  team_id: string | null;
  stage: EnquiryStage;
  resolution_category: EnquiryResolution | null;
  resolution_notes: string | null;
  merged_into_id: string | null;
  codename?: string | null;
  codename_is_custom?: boolean;
  client_code?: string | null;
  raw_payload: EnquiryRawPayload;
  classification_result: ClassificationResult | null;
  extracted_candidates: ExtractedCandidatesPayload | null;
  enquiry_metadata: EnquiryMetadata | null;
  created_at: string | null;
  updated_at: string | null;
  resolved_at: string | null;
  report_request_ids: string[];
  report_requests?: ApiEnquiryChildRequest[];
}

export function transformToEnquirySummary(api: ApiEnquirySummary): EnquirySummary {
  return {
    id: api.id,
    source: api.source,
    sourceExternalId: api.source_external_id,
    teamId: api.team_id,
    stage: api.stage,
    resolutionCategory: api.resolution_category,
    candidateCount: api.candidate_count,
    codename: api.codename ?? null,
    codenameFamily: api.codename_family ?? api.codename ?? null,
    createdAt: api.created_at,
    resolvedAt: api.resolved_at,
  };
}

export function transformToEnquiryDetail(api: ApiEnquiryDetail): EnquiryDetail {
  return {
    id: api.id,
    source: api.source,
    sourceExternalId: api.source_external_id,
    teamId: api.team_id,
    stage: api.stage,
    resolutionCategory: api.resolution_category,
    resolutionNotes: api.resolution_notes,
    mergedIntoId: api.merged_into_id,
    codename: api.codename ?? null,
    codenameIsCustom: api.codename_is_custom ?? false,
    clientCode: api.client_code ?? null,
    rawPayload: api.raw_payload ?? {},
    classificationResult: api.classification_result,
    extractedCandidates: api.extracted_candidates,
    enquiryMetadata: api.enquiry_metadata,
    createdAt: api.created_at,
    updatedAt: api.updated_at,
    resolvedAt: api.resolved_at,
    reportRequestIds: api.report_request_ids ?? [],
    reportRequests: (api.report_requests ?? []).map(transformToEnquiryChildRequest),
  };
}

/** A team that is the only one with users on the requester's email domain.
 * Offered to the analyst, never applied on its own. */
export interface EnquiryTeamSuggestion {
  teamId: string;
  domain: string;
  userCount: number;
}

/** Why a team could not be worked out from the requester. */
export type EnquiryTeamResolutionReason =
  | 'already_set'
  | 'no_requester_email'
  | 'internal_sender'
  | 'free_mail_domain'
  | 'no_team_on_domain'
  | 'several_teams_on_domain';

export interface ApiEnquiryTeamResolution {
  applied: boolean;
  team_id: string | null;
  suggestion: { team_id: string; domain: string; user_count: number } | null;
  reason: EnquiryTeamResolutionReason | null;
  enquiry: ApiEnquiryDetail;
}

/** Result of looking up an enquiry's team from the person who raised it. */
export interface EnquiryTeamResolution {
  /** True when the requester matched a Backoffice user and the team was set. */
  applied: boolean;
  teamId: string | null;
  suggestion: EnquiryTeamSuggestion | null;
  reason: EnquiryTeamResolutionReason | null;
  /** The enquiry as it stands after the lookup. */
  enquiry: EnquiryDetail;
}

export function transformToEnquiryTeamResolution(
  api: ApiEnquiryTeamResolution
): EnquiryTeamResolution {
  return {
    applied: api.applied,
    teamId: api.team_id,
    suggestion: api.suggestion
      ? {
          teamId: api.suggestion.team_id,
          domain: api.suggestion.domain,
          userCount: api.suggestion.user_count,
        }
      : null,
    reason: api.reason,
    enquiry: transformToEnquiryDetail(api.enquiry),
  };
}

const STAGE_LABELS: Record<EnquiryStage, string> = {
  received: 'Received',
  triaged: 'Triaged',
  resolved: 'Resolved',
};

const STAGE_COLORS: Record<EnquiryStage, string> = {
  received: 'gray',
  triaged: 'green',
  resolved: 'dark',
};

export function formatStage(stage: EnquiryStage): string {
  return STAGE_LABELS[stage] ?? stage;
}

export function stageColor(stage: EnquiryStage): string {
  return STAGE_COLORS[stage] ?? 'gray';
}

const RESOLUTION_LABELS: Record<EnquiryResolution, string> = {
  converted: 'Converted',
  quote_only: 'Quote only',
  out_of_scope: 'Out of scope',
  spam: 'Spam',
  merged: 'Merged',
  other: 'Other',
};

export function formatResolution(category: EnquiryResolution | null): string {
  if (!category) {
    return '-';
  }
  return RESOLUTION_LABELS[category] ?? category;
}

/**
 * Resolution categories an analyst may pick from the resolve form. Excludes
 * `converted` (only set via convert-to-requests) and is the source of truth
 * for the resolve UI's <Select>.
 */
export const RESOLVABLE_CATEGORIES: { value: EnquiryResolution; label: string }[] = [
  { value: 'quote_only', label: 'Quote only' },
  { value: 'out_of_scope', label: 'Out of scope' },
  { value: 'spam', label: 'Spam' },
  { value: 'merged', label: 'Merged' },
  { value: 'other', label: 'Other' },
];

// ---------------------------------------------------------------------------
// Client-facing enquiry (the portal path) — deliberately basic, mirrors
// ClientReportRequestSummary. Served by /client/enquiries.
// ---------------------------------------------------------------------------

export type ClientEnquiryStatus = 'submitted' | 'closed';

export interface ApiClientEnquiry {
  id: string;
  codename: string | null;
  status: ClientEnquiryStatus;
  subject: string | null;
  requester_email: string | null;
  created_at: string | null;
}

export interface ClientEnquiry {
  id: string;
  codename: string | null;
  status: ClientEnquiryStatus;
  subject: string | null;
  requesterEmail: string | null;
  createdAt: string | null;
}

export function transformToClientEnquiry(api: ApiClientEnquiry): ClientEnquiry {
  return {
    id: api.id,
    codename: api.codename,
    status: api.status,
    subject: api.subject,
    requesterEmail: api.requester_email,
    createdAt: api.created_at,
  };
}

/** Payload for POST /client/enquiries (the portal free-text intake). */
export interface ClientEnquiryCreatePayload {
  text: string;
  subjectHint?: string | null;
  draftId?: string | null;
  /** Admin-only team override, mirroring the direct-request submit. */
  teamId?: string | null;
}
